// Small per-module glyphs for the "⋯" recent-views menu (see `HistoryMenu` in UXView.tsx) — lets
// the menu be scanned by shape (tasks vs properties vs meetings…) instead of by re-reading text
// that's often truncated at the row's width. Hand-drawn inline SVGs to match this codebase's
// existing icon convention (see `TaskTypeIcon`/`ContextTypeIcon` in `Tasks/TaskCard.tsx`) — there
// is no icon library dependency here, so a new module must get its glyph added below rather than
// imported.
import type { ReactNode } from 'react';
import type { SamModule } from '@/components/SAM/samActionTypes';

const SVG_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor' as const,
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const PATHS: Partial<Record<SamModule, ReactNode>> = {
  tasks: (
    <><rect x="3" y="3" width="18" height="18" rx="3" /><polyline points="8 12 11 15 16 9" /></>
  ),
  my_todos: (
    <><path d="M9 6h11M9 12h11M9 18h11" /><path d="M4 5.5l1 1 2-2" /><path d="M4 11.5l1 1 2-2" /><path d="M4 17.5l1 1 2-2" /></>
  ),
  notes: (
    <><path d="M4 4h11l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" /><path d="M15 4v5h5" /><path d="M8 13h8M8 17h5" /></>
  ),
  properties: (
    <><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" /><polyline points="9 21 9 12 15 12 15 21" /></>
  ),
  meetings: (
    <><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M8 4v3M16 4v3M3 10h18" /><circle cx="12" cy="15" r="1.8" /></>
  ),
  calendar: (
    <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></>
  ),
  transactions: (
    <><path d="M4 7h13M13 3l4 4-4 4" /><path d="M20 17H7M11 21l-4-4 4-4" /></>
  ),
  proposals: (
    <><rect x="4" y="3.5" width="16" height="17" rx="3" /><path d="M8 8.5h8M8 12.5h8M8 16.5h4" /></>
  ),
  jobs: (
    <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>
  ),
  billing: (
    <><circle cx="12" cy="12" r="9" /><path d="M12 7v10" /><path d="M15 9.5c-.5-1-1.5-1.5-3-1.5-2 0-3 .9-3 2s1 1.6 3 2 3 .9 3 2-1 2-3 2c-1.5 0-2.5-.5-3-1.5" /></>
  ),
  notifications: (
    <><path d="M6 8a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 12 6 8z" /><path d="M9.5 17a2.5 2.5 0 0 0 5 0" /></>
  ),
  users: (
    <><circle cx="12" cy="8" r="3.2" /><path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" /></>
  ),
  roles: (
    <path d="M12 3l7 3v6c0 5-3.4 8.4-7 9-3.6-.6-7-4-7-9V6l7-3z" />
  ),
  communications: (
    <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 4V6z" />
  ),
  workflow: (
    <><circle cx="6" cy="6" r="2.1" /><circle cx="6" cy="18" r="2.1" /><circle cx="18" cy="12" r="2.1" /><path d="M8 6h4a4 4 0 0 1 4 4M8 18h4a4 4 0 0 0 4-4" /></>
  ),
  engagements: (
    <><path d="M9 15l6-6" /><path d="M8.5 12.5l-2 2a3 3 0 0 0 4.2 4.2l2-2" /><path d="M15.5 11.5l2-2a3 3 0 0 0-4.2-4.2l-2 2" /></>
  ),
  contacts: (
    <><rect x="5" y="3" width="14" height="18" rx="2" /><circle cx="12" cy="10" r="2.2" /><path d="M8.5 16c.6-1.8 2-2.5 3.5-2.5s2.9.7 3.5 2.5" /><path d="M3 7h2M3 12h2M3 17h2" /></>
  ),
  warranties: (
    <><path d="M12 3l7 3v6c0 5-3.4 8.4-7 9-3.6-.6-7-4-7-9V6l7-3z" /><polyline points="9 12 11 14 15 9.5" /></>
  ),
  measurements: (
    <><path d="M3 16.5L16.5 3l4.5 4.5L7.5 21z" /><path d="M13 6.5l1.5 1.5M10 9.5l1.5 1.5M7 12.5l1.5 1.5" /></>
  ),
  media: (
    <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.8" /><path d="M21 16l-5.5-5.5a2 2 0 0 0-2.8 0L4 19" /></>
  ),
  quick_quote: (
    <polygon points="13 2 4 14 11 14 10 22 20 9 13 9 13 2" fill="currentColor" stroke="none" />
  ),
  assessments: (
    <><rect x="6" y="4" width="12" height="17" rx="2" /><rect x="9" y="2.5" width="6" height="3" rx="1" /><polyline points="9 13 11 15 15 10.5" /></>
  ),
};

/** Generic fallback for any module without a dedicated glyph above (e.g. a new `SamModule` added
 *  before its icon is). Never throws on an unrecognized value — the menu row degrades to a plain
 *  placeholder square rather than a blank slot. */
const DEFAULT_PATH = (
  <><rect x="4" y="4" width="7" height="7" rx="1" /><rect x="13" y="4" width="7" height="7" rx="1" /><rect x="4" y="13" width="7" height="7" rx="1" /><rect x="13" y="13" width="7" height="7" rx="1" /></>
);

export function ModuleIcon({ module, size = 14, className }: { module: SamModule | string; size?: number; className?: string }) {
  const path = PATHS[module as SamModule] ?? DEFAULT_PATH;
  return (
    <svg {...SVG_PROPS} width={size} height={size} className={`flex-shrink-0 ${className ?? ''}`} aria-hidden>
      {path}
    </svg>
  );
}
